<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { FunnelX } from 'lucide-vue-next';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import { formatCnpjMasked, formatCnpjTyping, normalizeCnpjAlphanumeric } from '@solfacil/frontend-utils/cnpj';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import DataTable, { type DataTableColumn } from '~/components/shared/DataTable.vue';
import useScrollToTop from '~/hooks/useScrollToTop';
import { PersonAPI } from '~/services/person-api/person-api.types';

import usePartnersFilters from '../hooks/usePartnersFilters';
import usePartners from '../queries/usePartners';

const router = useRouter();

const TABLE_COLUMNS: DataTableColumn[] = [
  { key: 'corporate_name', label: 'Razão Social', sort: true },
  { key: 'trade_name', label: 'Nome Fantasia', sort: true },
  { key: 'document', label: 'CNPJ', sort: true },
  { key: 'responsible', label: 'Responsável' },
  { key: 'sfplus', label: 'Level' },
  { key: 'operation_term_accepted', label: 'Termo Aceito' },
];

const {
  filterForm,
  filterParams,
  handleReset,
  handleSearch,
  initializeForm,
  searchParams,
  setPage,
  setPageSize,
} = usePartnersFilters();

const [documentModel, documentProps] = filterForm.defineField('document');
const [partialNameModel, partialNameProps] = filterForm.defineField('partial_name');

const currentPage = computed(() => Number(searchParams.value.page) || 1);
const pageSize = computed(() => Number(searchParams.value.page_size) || 20);

const apiParams = computed<PersonAPI.ListPartnersParams>(() => ({
  page: currentPage.value,
  page_size: pageSize.value,
  ...filterParams.value,
}));

const {
  data: partners,
  isLoading: isLoadingPartners,
} = usePartners(apiParams);

const tableData = computed(() => partners.value?.data ?? []);
const totalCount = computed(() => partners.value?.count || 0);
const hasNext = computed(() => currentPage.value < Math.ceil(totalCount.value / pageSize.value));

function formatBoolean(value: boolean): string {
  return value ? 'Sim' : 'Não';
}

function formatLevel(partner: PersonAPI.Partner): string {
  if (!partner.sfplus?.level || partner.sfplus.level === -2 || partner.sfplus.level === -1) return '-';
  return partner.sfplus.level >= 99 ? 'Black' : `Level ${partner.sfplus.level}`;
}

function handleCNPJInput(event: Event) {
  const input = event.target as HTMLInputElement;
  documentModel.value = formatCnpjTyping(input.value);
}

function handlePageChange(page: number) {
  setPage(page);
}

function handlePageSizeChange(newPageSize: number) {
  setPageSize(newPageSize);
}

function handleViewDetails(partner: Pick<PersonAPI.Partner, 'id'>) {
  router.push(`/partner/${partner.id}`);
}

useScrollToTop(currentPage);

onMounted(() => {
  document.title = 'Parceiros';
  initializeForm();
});
</script>

<template>
  <DashboardLayout title="Parceiros" description="Lista de parceiros">
    <div class="row row-cards">
      <div class="col-12">
        <div class="card">
          <DataTable
            :columns="TABLE_COLUMNS"
            :data="tableData"
            :loading="isLoadingPartners"
            :soft="false"
            :actions="['details']"
            row-key="id"
            paginated
            :current-page="currentPage"
            :has-next="hasNext"
            :page-size="pageSize"
            empty-message="Nenhum parceiro encontrado"
            @details="handleViewDetails"
            @row-dblclick="handleViewDetails"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
          >
            <template #filters>
              <div class="filter-field">
                <div class="mb-3">
                  <label class="form-label" for="document">CNPJ</label>
                  <input
                    id="document"
                    v-model="documentModel"
                    v-bind="documentProps"
                    type="text"
                    class="form-control"
                    placeholder="00.000.000/0000-00"
                    maxlength="18"
                    @input="handleCNPJInput"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div class="filter-field">
                <div class="mb-3">
                  <label class="form-label" for="partial_name">Nome Fantasia</label>
                  <input
                    id="partial_name"
                    v-model="partialNameModel"
                    v-bind="partialNameProps"
                    type="text"
                    class="form-control"
                    placeholder="Nome Fantasia"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>
            </template>

            <template #filters-actions>
              <div class="filter-field">
                <div class="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="handleReset"
                  >
                    <FunnelX class="icon icon-sm" />
                    Limpar
                  </button>
                </div>
              </div>
            </template>

            <template #cell-corporate_name="{ item }">
              <span class="text-reset fw-bold">{{ (item as PersonAPI.Partner).corporate_name }}</span>
            </template>

            <template #cell-document="{ item }">
              <span class="text-secondary">
                {{ formatCnpjMasked(normalizeCnpjAlphanumeric((item as PersonAPI.Partner).document)) }}
              </span>
            </template>

            <template #cell-responsible="{ item }">
              <div>
                <div class="text-reset">{{ (item as PersonAPI.Partner).responsible.fullname }}</div>
                <div class="text-secondary small">{{ (item as PersonAPI.Partner).responsible.contact.email }}</div>
              </div>
            </template>

            <template #cell-sfplus="{ item }">
              <span
                v-if="formatLevel(item as PersonAPI.Partner) === '-'"
                class="text-secondary"
              >
                {{ formatLevel(item as PersonAPI.Partner) }}
              </span>
              <span
                v-else-if="formatLevel(item as PersonAPI.Partner) === 'Black'"
                class="badge bg-secondary-lt fw-bold"
              >
                {{ formatLevel(item as PersonAPI.Partner) }}
              </span>
              <span
                v-else
                class="badge bg-info-lt"
              >
                {{ formatLevel(item as PersonAPI.Partner) }}
              </span>
            </template>

            <template #cell-operation_term_accepted="{ item }">
              <span
                :class="`badge bg-${(item as PersonAPI.Partner).operation_term_accepted ? 'success' : 'warning'}-lt`"
              >
                {{ formatBoolean((item as PersonAPI.Partner).operation_term_accepted) }}
              </span>
            </template>

          </DataTable>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
.filter-field {
  min-width: 200px;
}

:deep(.data-table__table) {
  th,
  td {
    white-space: nowrap;
  }
}
</style>
